import { useState } from 'react';
import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';

import { sendUserMessage } from '../../xstate/app.xstate-utils';
import {
  proStudioService,
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { waitForProStudioIdle } from '../../xstate/wait-for-pro-studio-idle';
import type { ProStudioContext } from '../../xstate/prostudio.xstate.types';
import { PROJECT_DATA_LOADED_IDLE_STATE } from '../../hooks/loading-states';
import { checkForErrors } from '../../utils';
import {
  type AgentKey,
  type KnowledgeSettings,
  type SupervisorSettings,
  readKnowledgeSettings,
  readSupervisorSettings,
  withSettingsBlock,
  withoutSettingsBlock,
} from './agent-settings-model';
import { findModelNode, readPrompt, withPrompt } from './agent-prompt';
import {
  type VersionText,
  type WriteDeps,
  buildTextDerive,
  writeVersionText,
} from './agent-settings-write';
import { toSolutionVersionInput } from '../../stores/utils/task-utils';
import {
  NodeColumns,
  NodeColumnsMap,
  tableStore,
} from '../../stores/table-store';
import { getJsonOrderMap } from '../../components/table/utils';
import {
  type AgentIdentity,
  type IdentityFields,
  identityTarget,
  legacyIdentityFields,
  readIdentity,
  templateParameterInput,
} from './agent-identity';

const READ = {
  supervisor: readSupervisorSettings,
  knowledge: readKnowledgeSettings,
} as const;

/** The solution-level text older saves wrote each agent's block into. */
const LEGACY_FIELD = {
  supervisor: 'otherInformation',
  knowledge: 'knowledgeAgentContext',
} as const;

const SAVE_TIMEOUT_MS = 20_000;

type VersionField = keyof VersionText;
type StoredVersion = NonNullable<ProStudioContext['draftVersionSolutionData']>;

/**
 * What a text write reaches in the app: the machine, the backend read, the
 * mutation input. Built the way task edits build theirs
 * (`toSolutionVersionInput`): the mutation REPLACES the version's files, so a
 * version whose files did not load is not saved at all rather than saved with
 * none.
 */
const versionWriteDeps = (
  setSaving: (saving: boolean) => void
): WriteDeps<StoredVersion> => ({
  waitForIdle: () => waitForProStudioIdle(proStudioService),
  isIdle: () => proStudioService.state.matches(PROJECT_DATA_LOADED_IDLE_STATE),
  currentVersionId: () =>
    proStudioService.getSnapshot().context.draftVersionSolutionData?.id,
  read: async (versionId) => {
    const response = await urqlGql.getSolutionVersionById({
      solutionVersionByIdId: versionId,
    });
    checkForErrors(response);
    return response.data?.admin_?.solutionVersionById as
      | StoredVersion
      | undefined;
  },
  toInput: (version) =>
    toSolutionVersionInput(
      version,
      (version.tasks ?? []).map((task) => ({
        id: task.id || undefined,
        name: task.name || '',
        description: task.description || '',
      }))
    ),
  send: (version, done) =>
    sendProStudioEvent({
      type: 'updateSolution',
      payload: { version, cb: done },
    }),
  notify: (text, autoClose) =>
    sendUserMessage({ type: 'error', text, autoClose }),
  setSaving,
  giveUpMs: SAVE_TIMEOUT_MS,
});

/** A row's new Parameter Input (JSON text), saved at once. */
export const saveParameterInput = (rowId: string, next: string): void => {
  tableStore.recordRowEdit(
    rowId,
    'Edit agent prompt',
    () => {
      tableStore.updateRow({
        rowId,
        updates: {
          [NodeColumns.parameterInput]: next as unknown as object,
          [NodeColumnsMap.parameterInputMap]: getJsonOrderMap({
            value: next,
            columnId: NodeColumns.parameterInput,
          }),
        },
        skipValidationRules: false,
        saveImmediately: true,
      });
    },
    NodeColumns.parameterInput
  );
};

/** A row's Parameter Input with its prompt replaced, saved at once. */
const savePrompt = (rowId: string, raw: unknown, prompt: string): boolean => {
  const next = withPrompt(raw, prompt);
  if (next === null) return false;
  saveParameterInput(rowId, next);
  return true;
};

/**
 * The two agents' settings and prompts, read from the solution, and a save
 * that writes them back.
 *
 * Each agent's choices live as a managed block in the system prompt its
 * model node sends (`agent-prompt.ts`), so they reach the model. Solutions
 * saved before that kept the block in `otherInformation` /
 * `knowledgeAgentContext`, which the prompt never reads: those are still read
 * when the prompt has no block, and the old block is removed on the next save.
 * A solution with no such node keeps the old place.
 */
export const useAgentSettings = () => {
  const data = useProStudioCtxSelector((ctx) => ctx.draftVersionSolutionData);
  const [saving, setSaving] = useState(false);

  const agentState = (agent: AgentKey) => {
    const node = findModelNode(tableStore.rows, agent);
    const prompt = node ? readPrompt(node.parameterInput) : null;
    const legacy = data?.[LEGACY_FIELD[agent]];
    const inPrompt = prompt === null ? null : READ[agent](prompt);
    const settings = inPrompt?.managed ? inPrompt : READ[agent](legacy);
    return {
      node: prompt === null ? undefined : node,
      prompt,
      managed: settings.managed,
      values: settings.values,
      legacyManaged: READ[agent](legacy).managed,
    };
  };
  const supervisor = agentState('supervisor');
  const knowledge = agentState('knowledge');

  /**
   * The solution-level text, for saves that still go there. `derive` gets the
   * text as the backend holds it when the save runs, not the copy this panel
   * loaded (`agent-settings-write.ts`).
   */
  const updateVersion = (
    derive: (stored: VersionText) => Partial<VersionText>
  ): Promise<boolean> =>
    data?.id
      ? writeVersionText(data.id, derive, versionWriteDeps(setSaving))
      : Promise.resolve(false);

  /**
   * Whole solution-level fields (the agent identity: brand, assistant name,
   * tone, and the texts that carry them), for saves the text writer above
   * does not cover. Waits for the machine first: `updateSolution` is dropped
   * unless it is idle, and the tasks and documents save just before this one
   * leaves it busy.
   */
  const updateVersionFields = async (
    fields: IdentityFields | ((now: StoredVersion) => IdentityFields)
  ): Promise<boolean> => {
    try {
      await waitForProStudioIdle(proStudioService);
    } catch {
      sendUserMessage({
        type: 'error',
        text: 'Couldn’t save the agent settings: Pro Studio is busy. Try again.',
        autoClose: 5000,
      });
      return false;
    }
    return new Promise((resolve) => {
      // The version as it is NOW, not as this render saw it: a Save that
      // writes in turn (tasks and documents, then these) must build on the
      // write before it, or it puts back what that write changed.
      const now =
        proStudioService.getSnapshot().context.draftVersionSolutionData;
      if (!now) {
        resolve(false);
        return;
      }
      // Built the way task edits are (`toSolutionVersionInput`): the
      // mutation REPLACES the version's files, so a draft whose files did not
      // load is not saved at all rather than saved with none.
      const base = toSolutionVersionInput(
        now,
        (now.tasks ?? []).map((task) => ({
          id: task.id || undefined,
          name: task.name || '',
          description: task.description || '',
        }))
      );
      if (!base) {
        sendUserMessage({
          type: 'error',
          text: 'Couldn’t save the agent settings: the solution’s files haven’t loaded. Reload and try again.',
          autoClose: 6000,
        });
        resolve(false);
        return;
      }
      const version = {
        ...base,
        ...(typeof fields === 'function' ? fields(now) : fields),
      } as typeof base;
      setSaving(true);
      // `updateSolution` calls back only on success; a failure lands in the
      // machine's error state, which reports it. Don't wait forever for it.
      const giveUp = setTimeout(() => {
        setSaving(false);
        sendUserMessage({
          type: 'error',
          text: 'Couldn’t save the agent settings. Try again.',
          autoClose: 5000,
        });
        resolve(false);
      }, SAVE_TIMEOUT_MS);
      sendProStudioEvent({
        type: 'updateSolution',
        payload: {
          version,
          cb: () => {
            clearTimeout(giveUp);
            setSaving(false);
            resolve(true);
          },
        },
      });
    });
  };

  const save = async (next: {
    supervisor?: SupervisorSettings;
    knowledge?: KnowledgeSettings;
    /** The designer's prompt text, without the settings block. */
    prompts?: Partial<Record<AgentKey, string>>;
  }): Promise<boolean> => {
    // What each solution-level text becomes, from the text as stored.
    const legacy: Partial<Record<VersionField, (stored: string) => string>> =
      {};
    const states = { supervisor, knowledge };
    (['supervisor', 'knowledge'] as const).forEach((agent) => {
      const values = next[agent];
      const text = next.prompts?.[agent];
      if (!values && text === undefined) return;
      const state = states[agent];
      const field = LEGACY_FIELD[agent];
      if (state.node && state.prompt !== null) {
        const body = text ?? withoutSettingsBlock(state.prompt);
        // A prompt edit alone doesn't add a block the solution never had.
        const prompt =
          values || state.managed
            ? withSettingsBlock(body, values ?? state.values)
            : body;
        savePrompt(state.node.id, state.node.parameterInput, prompt);
        if (state.legacyManaged) {
          legacy[field] = withoutSettingsBlock;
        }
      } else if (values) {
        legacy[field] = (stored) => withSettingsBlock(stored, values);
      }
    });
    const fields = Object.keys(legacy) as VersionField[];
    return fields.length ? updateVersion(buildTextDerive(legacy)) : true;
  };

  const target = identityTarget(tableStore.rows);
  const identity = readIdentity(target, data);

  /**
   * Who the agents speak for (`agent-identity.ts`), written where this
   * solution reads it: the template's variables step, or an older
   * solution's fields and config row.
   */
  const saveIdentity = async (next: AgentIdentity): Promise<boolean> => {
    if (target.kind === 'template') {
      saveParameterInput(
        target.row.id,
        templateParameterInput(target.row, next)
      );
      // Design's summary reads the version fields; keep them in step.
      return updateVersionFields({
        brandName: next.brandName.trim(),
        supervisorName: next.assistantName.trim(),
      });
    }
    // From the version as it is when the write runs, so the settings blocks
    // it keeps are the current ones.
    const saved = await updateVersionFields((now) =>
      legacyIdentityFields(next, now)
    );
    if (saved && target.row) {
      tableStore.updateSupervisorConfigRow({
        brandName: next.brandName.trim(),
        supervisorName: next.assistantName.trim(),
        knowledgeAgentTone: (next.tone ?? '').trim(),
      });
    }
    return saved;
  };

  return {
    loaded: !!data,
    identity,
    saveIdentity,
    supervisor: supervisor.values as SupervisorSettings,
    knowledge: knowledge.values as KnowledgeSettings,
    /** Each agent's prompt without the settings block; null with no node. */
    prompts: {
      supervisor:
        supervisor.prompt === null
          ? null
          : withoutSettingsBlock(supervisor.prompt),
      knowledge:
        knowledge.prompt === null
          ? null
          : withoutSettingsBlock(knowledge.prompt),
    } as Record<AgentKey, string | null>,
    files: data?.files ?? [],
    model: data?.llmCredential?.llmProvider?.name ?? 'Gemini',
    temperature: data?.temperature,
    saving,
    save,
  };
};
